/**
 * Catálogo central de la API v1 (alineado con `facturacion_electronica_backend`).
 *
 * - `paths`: rutas relativas (prefijo `/api/v1`).
 * - `api`: funciones que ejecutan fetch vía `httpClient` (listar / crear / editar / eliminar cuando aplique).
 *
 * Auth por defecto: Bearer salvo métodos marcados con `{ auth: false }`.
 */

import {
  apiDelete,
  apiGet,
  apiGetBlob,
  apiGetText,
  apiPatch,
  apiPatchForm,
  apiPost,
  apiPostBlob,
  apiPostForm,
  apiPut,
  apiRequest,
} from "./httpClient";

/** Rutas estáticas y builders por recurso */
export const paths = {
  auth: {
    register: "/api/v1/auth/register",
    login: "/api/v1/auth/login",
    loginVerifyCode: "/api/v1/auth/login/verify-code",
    loginResendCode: "/api/v1/auth/login/resend-code",
    logout: "/api/v1/auth/logout",
    refresh: "/api/v1/auth/refresh",
    verifyEmail: "/api/v1/auth/verify-email",
    forgotPassword: "/api/v1/auth/forgot-password",
    resetPassword: "/api/v1/auth/reset-password",
    me: "/api/v1/me",
    changePassword: "/api/v1/me/change-password",
  },

  organizations: {
    collection: "/api/v1/organizations",
    one: (orgUuid) => `/api/v1/organizations/${orgUuid}`,
    apiKeys: (orgUuid) => `/api/v1/organizations/${orgUuid}/api-keys`,
    apiKeyRegenerate: (orgUuid, apiKeyUuid) =>
      `/api/v1/organizations/${orgUuid}/api-keys/${apiKeyUuid}/regenerate`,
    apiKeyOne: (orgUuid, apiKeyUuid) =>
      `/api/v1/organizations/${orgUuid}/api-keys/${apiKeyUuid}`,
    cronJobs: (orgUuid) => `/api/v1/organizations/${orgUuid}/cron-jobs`,
    cronJobsBootstrap: (orgUuid) =>
      `/api/v1/organizations/${orgUuid}/cron-jobs/bootstrap`,
    cronJobOne: (orgUuid, cronJobUuid) =>
      `/api/v1/organizations/${orgUuid}/cron-jobs/${cronJobUuid}`,
    members: (orgUuid) => `/api/v1/organizations/${orgUuid}/members`,
    memberOne: (orgUuid, memberId) =>
      `/api/v1/organizations/${orgUuid}/members/${memberId}`,
    mercadoPago: (orgUuid) => `/api/v1/organizations/${orgUuid}/mercadopago`,
    shopify: (orgUuid) => `/api/v1/organizations/${orgUuid}/shopify`,
    shopifyDiagnostico: (orgUuid) =>
      `/api/v1/organizations/${orgUuid}/shopify/diagnostico`,
    programarSync: (orgUuid) =>
      `/api/v1/organizations/${orgUuid}/integraciones/programar`,
    mercadoLibre: (orgUuid) => `/api/v1/organizations/${orgUuid}/mercadolibre`,
    mercadoLibreConectar: (orgUuid) =>
      `/api/v1/organizations/${orgUuid}/mercadolibre/conectar`,
  },

  dteDocumentPayments: "/api/v1/dte-documents/pagos",

  mercadoPago: {
    pagos: "/api/v1/mercadopago/pagos",
    sync: "/api/v1/mercadopago/pagos/sync",
    conciliar: "/api/v1/mercadopago/pagos/conciliar",
    vinculo: (id) => `/api/v1/mercadopago/pagos/${id}/vinculo`,
    descartar: (id) => `/api/v1/mercadopago/pagos/${id}/descartar`,
  },

  shopify: {
    ordenes: "/api/v1/shopify/ordenes",
    sync: "/api/v1/shopify/ordenes/sync",
  },

  cafStock: {
    collection: "/api/v1/caf-stock",
    upload: "/api/v1/caf-stock/upload",
    deleteByFolio: "/api/v1/caf-stock/by-folio",
    deleteByUuid: (cafUuid) => `/api/v1/caf-stock/by-uuid/${cafUuid}`,
    one: (id) => `/api/v1/caf-stock/${id}`,
  },

  folios: {
    sequences: "/api/v1/folio-sequences",
    sequenceSync: "/api/v1/folio-sequences/sync",
    sequenceOne: (id) => `/api/v1/folio-sequences/${id}`,
    reservations: "/api/v1/folio-reservations",
    reservationOne: (id) => `/api/v1/folio-reservations/${id}`,
  },

  dteDocuments: {
    collection: "/api/v1/dte-documents",
    export: "/api/v1/dte-documents/export",
    exportRepaired: "/api/v1/dte-documents/export-repaired",
    exportDetail: "/api/v1/dte-documents/export-detail",
    statsEmissions: "/api/v1/dte-documents/stats/emissions",
    statsSalesHeatmap: "/api/v1/dte-documents/stats/sales-heatmap",
    one: (id) => `/api/v1/dte-documents/${id}`,
    hide: (id) => `/api/v1/dte-documents/${id}/hide`,
    unhide: (id) => `/api/v1/dte-documents/${id}/unhide`,
    repairWithCurrentCaf: (id) =>
      `/api/v1/dte-documents/${id}/repair-with-current-caf`,
    trackingEnqueue: (id) => `/api/v1/dte-documents/${id}/tracking/enqueue`,
    deliveryEnqueue: (id) => `/api/v1/dte-documents/${id}/delivery/enqueue`,
    deliverySend: (id) => `/api/v1/dte-documents/${id}/delivery/send`,
    uploadRetry: (id) => `/api/v1/dte-documents/${id}/upload/retry`,
    uploadRetryEnqueue: (id) => `/api/v1/dte-documents/${id}/upload-retry/enqueue`,
  },

  dteDailyBooks: {
    collection: "/api/v1/dte-daily-books",
    one: (id) => `/api/v1/dte-daily-books/${id}`,
  },

  dteMonthlyBooks: {
    collection: "/api/v1/dte-monthly-books",
    one: (id) => `/api/v1/dte-monthly-books/${id}`,
  },

  emissionProfiles: {
    collection: "/api/v1/dte-emission-profiles",
    upload: "/api/v1/dte-emission-profiles/upload",
    uploadCertificate: (id) =>
      `/api/v1/dte-emission-profiles/${id}/upload-certificate`,
    requestCertificateDownloadCode: (id) =>
      `/api/v1/dte-emission-profiles/${id}/certificate/request-code`,
    resendCertificateDownloadCode: (id) =>
      `/api/v1/dte-emission-profiles/${id}/certificate/resend-code`,
    downloadCertificate: (id) =>
      `/api/v1/dte-emission-profiles/${id}/certificate/download`,
    one: (id) => `/api/v1/dte-emission-profiles/${id}`,
  },

  gmailInbox: {
    collection: "/api/v1/gmail-dte-inbox",
    resync: "/api/v1/gmail-dte-inbox/resync",
    resumen: "/api/v1/gmail-dte-inbox/resumen",
    xml: (id) => `/api/v1/gmail-dte-inbox/${id}/xml`,
    topProveedores: "/api/v1/gmail-dte-inbox/top-proveedores",
    one: (id) => `/api/v1/gmail-dte-inbox/${id}`,
    regeneratePdf: (id) => `/api/v1/gmail-dte-inbox/${id}/regenerate-pdf`,
    reprocess: (id) => `/api/v1/gmail-dte-inbox/${id}/reprocess`,
    manualAccept: (id) => `/api/v1/gmail-dte-inbox/${id}/manual-accept`,
    respuestaComercial: (id) => `/api/v1/gmail-dte-inbox/${id}/respuesta-comercial`,
    exportDetail: "/api/v1/gmail-dte-inbox/export-detail",
    intercambioSettings: "/api/v1/gmail-intercambio/settings",
  },

  emissions: {
    boletas: "/api/v1/boletas",
    boletasTrack: "/api/v1/boletas/track-envio-boleta",
    facturas: "/api/v1/facturas",
    guiasDespacho: "/api/v1/guias-despacho",
    notasCredito: "/api/v1/notas-credito",
    notasDebito: "/api/v1/notas-debito",
    intercambioCompras: "/api/v1/intercambio-compras",
  },

  libros: {
    ventasDailyFill: "/api/v1/libros-ventas/daily/fill",
    ventasMonthlySend: "/api/v1/libros-ventas/monthly/send",
    comprasDailyFill: "/api/v1/libros-compras/daily/fill",
    comprasMonthlySend: "/api/v1/libros-compras/monthly/send",
    boletasDailyFill: "/api/v1/libros-boletas/daily/fill",
    boletasMonthlySend: "/api/v1/libros-boletas/monthly/send",
    guiasDailyFill: "/api/v1/libros-guias/daily/fill",
    guiasMonthlySend: "/api/v1/libros-guias/monthly/send",
  },
};

function withQueryString(path, query = {}) {
  const entries = Object.entries(query).filter(
    ([, v]) => v !== undefined && v !== null && String(v) !== ""
  );
  if (!entries.length) return path;
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
  return `${path}?${qs}`;
}

/** ——— Auth (público / sesión) ——— */
export const authApi = {
  register: (body) => apiPost(paths.auth.register, body, { auth: false }),
  login: (body) => apiPost(paths.auth.login, body, { auth: false }),
  verifyLoginCode: (body) =>
    apiPost(paths.auth.loginVerifyCode, body, { auth: false }),
  resendLoginCode: (body) =>
    apiPost(paths.auth.loginResendCode, body, { auth: false }),
  logout: (body) => apiPost(paths.auth.logout, body ?? {}, { auth: false }),
  refresh: (body) => apiPost(paths.auth.refresh, body, { auth: false }),
  verifyEmail: (body) =>
    apiPost(paths.auth.verifyEmail, body, { auth: false }),
  verifyEmailGet: (queryString = "") =>
    apiRequest("GET", `${paths.auth.verifyEmail}${queryString}`, {
      auth: false,
    }),
  forgotPassword: (body) =>
    apiPost(paths.auth.forgotPassword, body, { auth: false }),
  resetPassword: (body) =>
    apiPost(paths.auth.resetPassword, body, { auth: false }),
  me: () => apiGet(paths.auth.me),
  changePassword: (body) => apiPost(paths.auth.changePassword, body),
};

/** ——— Organizaciones ——— */
export const organizationsApi = {
  list: () => apiGet(paths.organizations.collection),
  create: (body) => apiPost(paths.organizations.collection, body),
  get: (orgUuid) => apiGet(paths.organizations.one(orgUuid)),
  update: (orgUuid, body) => apiPatch(paths.organizations.one(orgUuid), body),
  remove: (orgUuid) => apiDelete(paths.organizations.one(orgUuid)),
};

/** ——— API keys por organización ——— */
export const organizationApiKeysApi = {
  list: (orgUuid, query = {}) =>
    apiGet(withQueryString(paths.organizations.apiKeys(orgUuid), query || {})),
  create: (orgUuid, body) =>
    apiPost(paths.organizations.apiKeys(orgUuid), body),
  regenerate: (orgUuid, apiKeyUuid) =>
    apiPost(paths.organizations.apiKeyRegenerate(orgUuid, apiKeyUuid), {}),
  revoke: (orgUuid, apiKeyUuid) =>
    apiDelete(paths.organizations.apiKeyOne(orgUuid, apiKeyUuid)),
};

/** ——— Miembros de la organización ——— */
export const organizationMembersApi = {
  list: (orgUuid) => apiGet(paths.organizations.members(orgUuid)),
  updateNotify: (orgUuid, memberId, notifyCompras) =>
    apiPatch(paths.organizations.memberOne(orgUuid, memberId), {
      notify_compras: notifyCompras,
    }),
  // Alta: si la persona no tiene cuenta, el backend la crea y le manda un
  // enlace para que elija su contraseña. Nunca se manejan contraseñas acá.
  add: (orgUuid, { email, role, fullName }) =>
    apiPost(paths.organizations.members(orgUuid), {
      email,
      role,
      ...(fullName ? { full_name: fullName } : {}),
    }),
  resendInvite: (orgUuid, memberId) =>
    apiPost(`${paths.organizations.memberOne(orgUuid, memberId)}/invite`, {}),
  updateAccess: (orgUuid, memberId, { role, estado }) =>
    apiPatch(paths.organizations.memberOne(orgUuid, memberId), {
      ...(role ? { role } : {}),
      ...(estado ? { estado } : {}),
    }),
};

/** ——— Info de pago de los documentos que se están mostrando ——— */
export const dteDocumentPaymentsApi = {
  // Va aparte del listado: ese endpoint es el camino mas usado del panel y no
  // conviene cargarlo con joins por un dato secundario.
  byIds: (ids) => apiGet(`${paths.dteDocumentPayments}?ids=${ids.join(",")}`),
};

/** ——— Mercado Pago: conciliación de pagos ——— */
export const mercadoPagoApi = {
  // La respuesta NUNCA trae el Access Token, solo si hay uno y sus últimos 4.
  getSettings: (orgUuid) => apiGet(paths.organizations.mercadoPago(orgUuid)),
  // Omitir accessToken conserva el que ya estaba: así se puede cambiar la
  // ventana de días sin volver a escribir el token.
  saveSettings: (orgUuid, { accessToken, habilitado, matchDiasVentana, syncDiasAtras }) =>
    apiPut(paths.organizations.mercadoPago(orgUuid), {
      ...(accessToken ? { accessToken } : {}),
      ...(habilitado != null ? { habilitado } : {}),
      ...(matchDiasVentana != null ? { matchDiasVentana } : {}),
      ...(syncDiasAtras != null ? { syncDiasAtras } : {}),
    }),
  deleteCredential: (orgUuid) =>
    apiDelete(paths.organizations.mercadoPago(orgUuid)),

  listPagos: (params = {}) => {
    const query = new URLSearchParams();
    for (const [clave, valor] of Object.entries(params)) {
      if (valor != null && valor !== "") query.set(clave, String(valor));
    }
    const qs = query.toString();
    return apiGet(qs ? `${paths.mercadoPago.pagos}?${qs}` : paths.mercadoPago.pagos);
  },
  sync: (body = {}) => apiPost(paths.mercadoPago.sync, body),
  conciliar: (body = {}) => apiPost(paths.mercadoPago.conciliar, body),
  vincular: (id, dteDocumentId) =>
    apiPost(paths.mercadoPago.vinculo(id), { dteDocumentId }),
  descartar: (id) => apiPost(paths.mercadoPago.descartar(id), {}),
};

/** ——— Shopify: puente para conciliar ——— */
export const shopifyApi = {
  getSettings: (orgUuid) => apiGet(paths.organizations.shopify(orgUuid)),
  // Shopify ya no entrega tokens permanentes: se guardan Client ID y Secret,
  // y el token se renueva solo cada 24h. accessToken queda para las tiendas
  // que todavía tengan un token antiguo.
  saveSettings: (orgUuid, {
    clientId, clientSecret, accessToken, shopDomain, habilitado, atributoPaymentId, syncDiasAtras,
  }) =>
    apiPut(paths.organizations.shopify(orgUuid), {
      ...(clientId ? { clientId } : {}),
      ...(clientSecret ? { clientSecret } : {}),
      ...(accessToken ? { accessToken } : {}),
      ...(shopDomain ? { shopDomain } : {}),
      ...(habilitado != null ? { habilitado } : {}),
      ...(atributoPaymentId ? { atributoPaymentId } : {}),
      ...(syncDiasAtras != null ? { syncDiasAtras } : {}),
    }),
  deleteCredential: (orgUuid) => apiDelete(paths.organizations.shopify(orgUuid)),
  // Dice si el nombre del atributo está bien puesto. Sin eso, el cruce exacto
  // no funciona y cae a monto y fecha.
  diagnostico: (orgUuid) => apiGet(paths.organizations.shopifyDiagnostico(orgUuid)),

  listOrdenes: (params = {}) => {
    const query = new URLSearchParams();
    for (const [clave, valor] of Object.entries(params)) {
      if (valor != null && valor !== "") query.set(clave, String(valor));
    }
    const qs = query.toString();
    return apiGet(qs ? `${paths.shopify.ordenes}?${qs}` : paths.shopify.ordenes);
  },
  sync: (body = {}) => apiPost(paths.shopify.sync, body),
  // Crea los cron jobs de sincronizacion. No agrega un worker: son dos
  // filas que ejecuta el worker de cron que ya corre.
  programarSync: (orgUuid) => apiPost(paths.organizations.programarSync(orgUuid), {}),
};

/** ——— MercadoLibre: pedidos como puente ——— */
export const mercadoLibreApi = {
  getSettings: (orgUuid) => apiGet(paths.organizations.mercadoLibre(orgUuid)),
  // Devuelve el enlace que el vendedor abre para autorizar. MercadoLibre no
  // permite conectar pegando credenciales: hay que pasar por el navegador.
  // Con accessToken conecta de una; sin el, devuelve el enlace de OAuth.
  conectar: (orgUuid, { clientId, clientSecret, accessToken }) =>
    apiPost(paths.organizations.mercadoLibreConectar(orgUuid), {
      ...(accessToken ? { accessToken } : {}),
      ...(clientId ? { clientId } : {}),
      ...(clientSecret ? { clientSecret } : {}),
    }),
  guardar: (orgUuid, { habilitado, syncDiasAtras }) =>
    apiPut(paths.organizations.mercadoLibre(orgUuid), {
      ...(habilitado != null ? { habilitado } : {}),
      ...(syncDiasAtras != null ? { syncDiasAtras } : {}),
    }),
  desconectar: (orgUuid) => apiDelete(paths.organizations.mercadoLibre(orgUuid)),
  sync: (body = {}) => apiPost("/api/v1/mercadolibre/pedidos/sync", body),
};

/** ——— Cron jobs por organización ——— */
export const organizationCronJobsApi = {
  list: (orgUuid) => apiGet(paths.organizations.cronJobs(orgUuid)),
  create: (orgUuid, body) =>
    apiPost(paths.organizations.cronJobs(orgUuid), body),
  bootstrap: (orgUuid, body) =>
    apiPost(paths.organizations.cronJobsBootstrap(orgUuid), body ?? {}),
  get: (orgUuid, cronJobUuid) =>
    apiGet(paths.organizations.cronJobOne(orgUuid, cronJobUuid)),
  update: (orgUuid, cronJobUuid, body) =>
    apiPatch(paths.organizations.cronJobOne(orgUuid, cronJobUuid), body),
  remove: (orgUuid, cronJobUuid) =>
    apiDelete(paths.organizations.cronJobOne(orgUuid, cronJobUuid)),
};

/** ——— CAF stock ——— */
export const cafStockApi = {
  list: (queryString = "") =>
    apiGet(`${paths.cafStock.collection}${queryString}`),
  create: (body) => apiPost(paths.cafStock.collection, body),
  uploadXml: (formData) => apiPostForm(paths.cafStock.upload, formData),
  get: (id) => apiGet(paths.cafStock.one(id)),
  update: (id, body) => apiPatch(paths.cafStock.one(id), body),
  remove: (id) => apiDelete(paths.cafStock.one(id)),
  deleteByFolio: () => apiDelete(paths.cafStock.deleteByFolio),
  deleteByUuid: (cafUuid) => apiDelete(paths.cafStock.deleteByUuid(cafUuid)),
};

/** ——— Folios ——— */
export const foliosApi = {
  listSequences: (queryString = "") =>
    apiGet(`${paths.folios.sequences}${queryString}`),
  syncSequences: (body) => apiPost(paths.folios.sequenceSync, body),
  getSequence: (id) => apiGet(paths.folios.sequenceOne(id)),
  listReservations: (queryString = "") =>
    apiGet(`${paths.folios.reservations}${queryString}`),
  getReservation: (id) => apiGet(paths.folios.reservationOne(id)),
};

/** ——— Documentos DTE ——— */
export const dteDocumentsApi = {
  list: (queryString = "") =>
    apiGet(`${paths.dteDocuments.collection}${queryString}`),
  emissionStats: (queryString = "") =>
    apiGet(`${paths.dteDocuments.statsEmissions}${queryString}`),
  salesHeatmap: (queryString = "") =>
    apiGet(`${paths.dteDocuments.statsSalesHeatmap}${queryString}`),
  exportCsv: (queryString = "") =>
    apiGetText(`${paths.dteDocuments.export}${queryString}`),
  exportRepairedXlsx: (queryString = "") =>
    apiGetBlob(`${paths.dteDocuments.exportRepaired}${queryString}`),
  exportDetailXlsx: (queryString = "") =>
    apiGetBlob(`${paths.dteDocuments.exportDetail}${queryString}`),
  get: (id) => apiGet(paths.dteDocuments.one(id)),
  update: (id, body) => apiPatch(paths.dteDocuments.one(id), body),
  remove: (id) => apiDelete(paths.dteDocuments.one(id)),
  hide: (id) => apiPost(paths.dteDocuments.hide(id), {}),
  unhide: (id) => apiPost(paths.dteDocuments.unhide(id), {}),
  repairWithCurrentCaf: (id, body) =>
    apiPost(paths.dteDocuments.repairWithCurrentCaf(id), body),
  enqueueTracking: (id) =>
    apiPost(paths.dteDocuments.trackingEnqueue(id), {}),
  enqueueDelivery: (id) =>
    apiPost(paths.dteDocuments.deliveryEnqueue(id), {}),
  // Reenvía el documento por correo (PDF + XML). Sin `to` va al correo que ya
  // tiene guardado el documento; con `to` se corrige la dirección.
  deliverySend: (id, body = {}) => apiPost(paths.dteDocuments.deliverySend(id), body),
  uploadRetry: (id) => apiPost(paths.dteDocuments.uploadRetry(id), {}),
  enqueueUploadRetry: (id) =>
    apiPost(paths.dteDocuments.uploadRetryEnqueue(id), {}),
};

/** ——— Libros diarios / mensuales (tablas dte-daily-books, dte-monthly-books) ——— */
export const dteBooksApi = {
  listDaily: (query = {}) =>
    apiGet(withQueryString(paths.dteDailyBooks.collection, query)),
  createDaily: (body) => apiPost(paths.dteDailyBooks.collection, body),
  getDaily: (id) => apiGet(paths.dteDailyBooks.one(id)),
  updateDaily: (id, body) => apiPatch(paths.dteDailyBooks.one(id), body),
  deleteDaily: (id) => apiDelete(paths.dteDailyBooks.one(id)),

  listMonthly: (query = {}) =>
    apiGet(withQueryString(paths.dteMonthlyBooks.collection, query)),
  createMonthly: (body) => apiPost(paths.dteMonthlyBooks.collection, body),
  getMonthly: (id) => apiGet(paths.dteMonthlyBooks.one(id)),
  updateMonthly: (id, body) => apiPatch(paths.dteMonthlyBooks.one(id), body),
  deleteMonthly: (id) => apiDelete(paths.dteMonthlyBooks.one(id)),
};

/** ——— Perfiles de emisión ——— */
export const emissionProfilesApi = {
  list: (query) => apiGet(withQueryString(paths.emissionProfiles.collection, query || {})),
  create: (body) => apiPost(paths.emissionProfiles.collection, body),
  upload: (formData) => apiPostForm(paths.emissionProfiles.upload, formData),
  get: (id) => apiGet(paths.emissionProfiles.one(id)),
  update: (id, body) => apiPatch(paths.emissionProfiles.one(id), body),
  remove: (id) => apiDelete(paths.emissionProfiles.one(id)),
  uploadCertificate: (id, formData) =>
    apiPatchForm(paths.emissionProfiles.uploadCertificate(id), formData),
  // Descarga verificada del certificado (solo admin): 1) pedir código al correo,
  // 2) reenviar (cooldown), 3) validar código y bajar el .pfx como blob.
  requestCertificateDownloadCode: (id) =>
    apiPost(paths.emissionProfiles.requestCertificateDownloadCode(id), {}),
  resendCertificateDownloadCode: (id, challengeId) =>
    apiPost(paths.emissionProfiles.resendCertificateDownloadCode(id), {
      challengeId,
    }),
  downloadCertificate: (id, { challengeId, code }) =>
    apiPostBlob(paths.emissionProfiles.downloadCertificate(id), {
      challengeId,
      code,
    }),
};

/** ——— Gmail DTE inbox ——— */
export const gmailDteInboxApi = {
  list: (query) =>
    apiGet(withQueryString(paths.gmailInbox.collection, query || {})),
  // Totales del conjunto FILTRADO, no de la página cargada: monto comprado
  // (con las notas de crédito restadas), pendientes por responder, y cuántas
  // compras no tienen monto legible.
  resumen: (query) =>
    apiGet(withQueryString(paths.gmailInbox.resumen, query || {})),
  // El XML vive en un bucket privado: se baja por la API, con la sesión.
  descargarXml: (id) => apiGetBlob(paths.gmailInbox.xml(id)),
  // Proveedores a los que más se compró, para el dashboard. Mismos filtros de
  // fecha que el resto de la pantalla.
  topProveedores: (query) =>
    apiGet(withQueryString(paths.gmailInbox.topProveedores, query || {})),
  create: (body) => apiPost(paths.gmailInbox.collection, body),
  resync: () => apiPost(paths.gmailInbox.resync, {}),
  regeneratePdf: (id) => apiPost(paths.gmailInbox.regeneratePdf(id), {}),
  // Reproceso: vuelve a bajar el correo original desde Gmail y lo procesa de
  // cero. Es la salida para las compras que quedaron en "error" sin XML ni PDF.
  reprocess: (id) => apiPost(paths.gmailInbox.reprocess(id), {}),
  manualAccept: (id) => apiPost(paths.gmailInbox.manualAccept(id), {}),
  // Respuesta comercial: aceptar, aceptar con discrepancias o rechazar.
  // El motivo viaja en el XML firmado que recibe el proveedor.
  respuestaComercial: (id, body) =>
    apiPost(paths.gmailInbox.respuestaComercial(id), body || {}),
  get: (id) => apiGet(paths.gmailInbox.one(id)),
  update: (id, body) => apiPatch(paths.gmailInbox.one(id), body),
  remove: (id) => apiDelete(paths.gmailInbox.one(id)),
  // Umbrales de aceptación manual (monto / antigüedad), editables por admin.
  getIntercambioSettings: () => apiGet(paths.gmailInbox.intercambioSettings),
  updateIntercambioSettings: (body) =>
    apiPatch(paths.gmailInbox.intercambioSettings, body),
  // Export Excel a nivel detalle (una fila por producto de cada compra).
  exportDetailXlsx: (queryString = "") =>
    apiGetBlob(`${paths.gmailInbox.exportDetail}${queryString}`),
};

/** ——— Emisión (solo creación / acción POST según backend) ——— */
export const emissionsApi = {
  createBoleta: (body) => apiPost(paths.emissions.boletas, body),
  trackBoleta: (body) => apiPost(paths.emissions.boletasTrack, body),
  createFactura: (body) => apiPost(paths.emissions.facturas, body),
  createGuia: (body) => apiPost(paths.emissions.guiasDespacho, body),
  createNotaCredito: (body) => apiPost(paths.emissions.notasCredito, body),
  createNotaDebito: (body) => apiPost(paths.emissions.notasDebito, body),
  intercambioCompras: (body) =>
    apiPost(paths.emissions.intercambioCompras, body),
};

/** ——— Libros SII (fill / send) ——— */
export const librosApi = {
  ventasDailyFill: (body) => apiPost(paths.libros.ventasDailyFill, body),
  ventasMonthlySend: (body) => apiPost(paths.libros.ventasMonthlySend, body),
  comprasDailyFill: (body) => apiPost(paths.libros.comprasDailyFill, body),
  comprasMonthlySend: (body) => apiPost(paths.libros.comprasMonthlySend, body),
  boletasDailyFill: (body) => apiPost(paths.libros.boletasDailyFill, body),
  boletasMonthlySend: (body) => apiPost(paths.libros.boletasMonthlySend, body),
  guiasDailyFill: (body) => apiPost(paths.libros.guiasDailyFill, body),
  guiasMonthlySend: (body) => apiPost(paths.libros.guiasMonthlySend, body),
};

/** Objeto único para importar todo */
export const api = {
  paths,
  auth: authApi,
  organizations: organizationsApi,
  organizationApiKeys: organizationApiKeysApi,
  organizationCronJobs: organizationCronJobsApi,
  dteDocumentPayments: dteDocumentPaymentsApi,
  mercadoPago: mercadoPagoApi,
  shopify: shopifyApi,
  mercadoLibre: mercadoLibreApi,
  cafStock: cafStockApi,
  folios: foliosApi,
  dteDocuments: dteDocumentsApi,
  dteBooks: dteBooksApi,
  emissionProfiles: emissionProfilesApi,
  gmailInbox: gmailDteInboxApi,
  emissions: emissionsApi,
  libros: librosApi,
};

export default api;
